import { useMemo } from 'react';

import { DataTransferCostItem } from '@cast/shared-types';
import { bytesToGiBiBytes, EMPTY_DATA_ITEM, toNumOrZero } from '@cast/utils';
import cloneDeep from 'lodash/cloneDeep';

import { NetworkTrafficBreakdownChartItem } from '../_types';

import { useWorkloadNetworkDataTransfer } from './useWorkloadNetworkDataTransfer';

const BYTES_KEYS: Array<{
  source: keyof DataTransferCostItem;
  target: keyof Omit<NetworkTrafficBreakdownChartItem, 'timestamp'>;
}> = [
  { source: 'intraZoneBytes', target: 'intraAz' },
  { source: 'interZoneBytes', target: 'crossAz' },
  { source: 'internetBytes', target: 'internet' },
  { source: 'interRegionBytes', target: 'crossRegion' },
  { source: 'cloudApiBytes', target: 'cloudApi' },
];

type AggregatedByTimestamp = Record<
  string,
  Omit<NetworkTrafficBreakdownChartItem, 'timestamp'>
>;

const aggregatorByTimestamp = (
  acc: AggregatedByTimestamp,
  data: DataTransferCostItem
): void => {
  const _timestamp = data.timestamp ?? '';

  if (!_timestamp) {
    return;
  }

  acc[_timestamp] ??= {
    intraAz: 0,
    crossAz: 0,
    internet: 0,
    crossRegion: 0,
    cloudApi: 0,
  };

  const item = { ...cloneDeep(EMPTY_DATA_ITEM), ...data };

  BYTES_KEYS.forEach(({ source, target }) => {
    acc[_timestamp][target] += toNumOrZero(item[source]);
  });
};

export const useNetworkTrafficBreakdownChartData = () => {
  const { data, isLoading, isFetching, error, refetch, stepSeconds } =
    useWorkloadNetworkDataTransfer();

  const chartData: NetworkTrafficBreakdownChartItem[] = useMemo(() => {
    const item = data?.item;

    if (!item) {
      return [];
    }

    const byTimestamp: AggregatedByTimestamp = {};

    item.egressMetrics?.forEach((metric) =>
      aggregatorByTimestamp(byTimestamp, metric)
    );
    item.ingressMetrics?.forEach((metric) =>
      aggregatorByTimestamp(byTimestamp, metric)
    );

    return Object.keys(byTimestamp)
      .sort()
      .map((timestamp) => ({
        timestamp,
        intraAz: bytesToGiBiBytes(byTimestamp[timestamp].intraAz),
        crossAz: bytesToGiBiBytes(byTimestamp[timestamp].crossAz),
        internet: bytesToGiBiBytes(byTimestamp[timestamp].internet),
        crossRegion: bytesToGiBiBytes(byTimestamp[timestamp].crossRegion),
        cloudApi: bytesToGiBiBytes(byTimestamp[timestamp].cloudApi),
      }));
  }, [data]);

  return {
    data: chartData,
    isLoading,
    isFetching,
    error,
    refetch,
    stepSeconds,
  };
};
